import * as angular from 'angular';
import * as _ from 'lodash';
import {LineCounts, LineEquipmentCounts, LineEquipmentItem, LineItem} from "../../models/counts/lineCounts";
import {Plant} from "../../models/navigation";
import DateUtilityService from "../../services/utility/dateutility.service";
import LineConfigsServiceName, {LineConfigsService} from "../../services/lineConfigs/lineConfigs.service";
import UIUtilityServiceName, {UIUtilityService} from "../../services/utility/uiUtility.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import CountsServiceName, {CountsService} from "../../services/counts/counts.service";
import {apsEnums} from "../../shared/dto/aps.enums";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import EquipmentServiceName, {EquipmentService} from "../../services/equipment/equipment.service";
import template from './equipmentRejects.tpl.html';
import InconsistentConfigController, {openInconsistentConfigurations} from "./inconsistentEquipmentConfigurations.controller";

let requires = [LineConfigsServiceName, UIUtilityServiceName, LoaderServiceName,
  CountsServiceName, EquipmentServiceName, InconsistentConfigController];

export interface EquipmentRejectsController {
  equipmentHealthState: any;
  equipment: LineEquipmentItem;
  close(): void;
  formatTime(utcTime: Date): string;
  canDisable: boolean;
  toggleActive(): void;
  viewInconsistencies(): void;
  isEquipmentWithInconsistencies: boolean;
}

export interface EquipmentRejectsControllerData {
  equipment: LineEquipmentItem;
  line: LineItem;
  lineConfig: LineCounts;
  plant: Plant;
  canDisable: boolean;
  shopId: string;
  detail: LineEquipmentCounts;
}

export function openEquipmentRejects(data: EquipmentRejectsControllerData,
                                     $modal: mm.foundation.modal): angular.IPromise<any> {
  return $modal.open({
    template: template,
    controller: 'EquipmentRejectsCtrl as vm',
    resolve: {
      data: () => data
    }
  }).result;
}

class EquipmentRejectsControllerImpl implements EquipmentRejectsController {
  equipment: LineEquipmentItem;
  line: LineItem;
  plant: Plant;
  equipmentHealthState: any;
  lineEquipmentName: string;
  canDisable: boolean;
  shopId: string;
  lineConfig: LineCounts;
  isEquipmentWithInconsistencies: boolean;
  detail: LineEquipmentCounts;
  showHealth: boolean;
  tabClass: string = 'er-details';

  private $modalInstance: mm.foundation.modalInstance;
  private lineConfigsService: LineConfigsService;
  private uiUtilityService: UIUtilityService;
  private $q: angular.IQService;
  private loaderService: LoaderService;
  private $modal: mm.foundation.modal;
  private countsService: CountsService;
  private equipmentService: EquipmentService;

  constructor(data: EquipmentRejectsControllerData,
              $modalInstance: mm.foundation.modalInstance,
              uiUtilityService: UIUtilityService,
              $q: angular.IQService,
              lineConfigsService: LineConfigsService,
              $modal: mm.foundation.modal,
              loaderService: LoaderService,
              countsService: CountsService,
              equipmentService: EquipmentService) {

    this.equipment = data.equipment;
    this.detail = data.detail;
    this.showHealth = this.detail && this.detail.tabs && this.detail.tabs.length > 0;
    this.line = data.line;
    this.plant = data.plant;
    this.canDisable = data.canDisable;

    this.setTabClass();

    this.shopId = data.shopId;
    this.$modalInstance = $modalInstance;
    this.lineConfigsService = lineConfigsService;
    this.uiUtilityService = uiUtilityService;
    this.$q = $q;
    this.equipmentHealthState = apsEnums.EquipmentHealthState;
    this.$modal = $modal;
    this.loaderService = loaderService;
    this.countsService = countsService;
    this.equipmentService = equipmentService;

    let equipmentMetaData: any = this.equipment.equipmentMetaData
      ? JSON.parse(this.equipment.equipmentMetaData)
      : null;

    this.lineEquipmentName = equipmentMetaData
      ? equipmentMetaData.Name
      : '';

    this.lineConfig = data.lineConfig;
    this.isEquipmentWithInconsistencies = this.setIsEquipmentWithInconsistencies(this.lineConfig);
  }

  private setTabClass(): void {
    let tabCount = 0;
    if (this.detail) {
      if (this.detail.tabs) {
        tabCount += this.detail.tabs.length;
      }
      if (this.detail.details && this.detail.details.length > 0) {
        tabCount++;
      }
    }
    if (tabCount <= 1) {
      this.tabClass = 'tab-title-muted';
    }
  }

  close(): void {
    this.$modalInstance.close();
  }

  formatTime(utcTime: Date): string {
    if (!utcTime) {
      return '-';
    }

    return DateUtilityService.formatTime(utcTime, this.plant.timeZoneId);
  }

  toggleActive(): void {
    if (!this.canDisable) {
      return;
    }

    let fullName = `${this.equipment.name}-${this.line.name}`;
    let confirmPromise: angular.IPromise<boolean>;
    if (!this.equipment.isDisabled) {
      confirmPromise = this.uiUtilityService.confirm(`Disable ${fullName}?`,
        `Are you sure you want to disable the ${this.equipment.name} on line ${this.line.name}?`);
    } else {
      confirmPromise = this.$q.when(true);
    }

    confirmPromise.then(confirmed => {
      if (!confirmed) {
        return;
      }

      let togglePromise = this.lineConfigsService.toggleLineEquipment({
        id: this.equipment.id,
        lineId: this.line.id,
        shopId: this.equipment.shopId,
        isDisabled: !this.equipment.isDisabled
      }).then(() => this.equipment.isDisabled = !this.equipment.isDisabled);
      this.loaderService.loadingPromise(togglePromise);
    });
  }

  viewInconsistencies(): void {
    openInconsistentConfigurations({
      shopId: this.shopId,
      equipmentId: this.equipment.equipmentId,
      lineConfig: this.lineConfig,
      equipmentName: this.equipment.name
    }, this.$modal);
  }

  /// NOTE: THIS LOGIC WILL SOON BE MOVED TO THE LINE COUNTS MODEL FOR REAL-TIME UPDATES
  setIsEquipmentWithInconsistencies(lineConfig: apsViewModels.ShopInspectionLines): boolean {
    if (!lineConfig.inconsistentEquipmentConfigurations) {
     return false;
    }

    return _.some(lineConfig.inconsistentEquipmentConfigurations, et => et === this.equipment.equipmentType);
  }
}

export default angular.module('aps.controller.equipmentRejects', requires)
  .controller('EquipmentRejectsCtrl', EquipmentRejectsControllerImpl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/counts/equipmentRejects.controller.ts